import React, { useEffect, useState } from 'react';
import { auth } from '../../firebase/firebase-config';
import { useTenant } from '../../context/TenantContext';
import { useUser } from '../../context/UserContext';
import { useMembership } from '../../context/MembershipContext';
import { isTenantAdminForTenant } from '../../platform/membershipPermissions';
import { markArticleAsRead } from '../../services/articlesService';
import { fetchUnitNameMap } from '../../services/unitsService';
import { describeArticleListPlacement } from '../../services/articleListGroups';
import ContentAttachmentsActions from '../../platform/ContentAttachmentsActions';
import ContentDetailHero from '../../platform/ContentDetailHero';
import ContentUnitTag from '../common/ContentUnitTag';
import ArticleAdminControls from './ArticleAdminControls';
import {
  isDetailHeroEnabled,
  PREMIUM_MODULE_IDS,
  premiumDetailClassName,
} from '../../platform/contentListVisual';

function formatDate(date) {
  if (!date) return '';
  const value = date instanceof Date ? date : new Date(date);
  return value.toLocaleString('sl-SI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ArticleDetail({ article: articleProp }) {
  const [article, setArticle] = useState(articleProp);
  const [unitNames, setUnitNames] = useState({});
  const { tenantId, config } = useTenant();
  const { user } = useUser();
  const { memberships } = useMembership();
  const isAdmin = isTenantAdminForTenant(memberships, tenantId, user);
  const useHero = isDetailHeroEnabled(PREMIUM_MODULE_IDS.ARTICLES);

  useEffect(() => {
    setArticle(articleProp);
  }, [articleProp]);

  const placementLabel = describeArticleListPlacement(article, config);

  useEffect(() => {
    const markAsRead = async () => {
      if (auth.currentUser?.uid) {
        await markArticleAsRead(auth.currentUser.uid, article.id);
      }
    };

    markAsRead();
  }, [article.id]);

  useEffect(() => {
    if (!tenantId) return;
    fetchUnitNameMap(tenantId).then(setUnitNames);
  }, [tenantId]);

  const meta = (
    <>
      {placementLabel && <span>{placementLabel}</span>}
      {placementLabel && <span className="platform-content-detail-hero__meta-separator">·</span>}
      <span>Ustvaril {article.displayName || 'Anonimen'}</span>
      {article.createdAt && (
        <>
          <span className="platform-content-detail-hero__meta-separator">·</span>
          <span>{formatDate(article.createdAt)}</span>
        </>
      )}
    </>
  );

  return (
    <article className={premiumDetailClassName(PREMIUM_MODULE_IDS.ARTICLES, 'article-detail platform-content-detail')}>
      <div className="platform-content-detail-shell">
        {useHero ? (
          <ContentDetailHero
            title={article.title}
            imageUrl={article.thumbnailUrl}
            badges={<ContentUnitTag item={article} unitNames={unitNames} />}
            meta={meta}
          />
        ) : null}

        <div className="platform-content-detail-card">
          {!useHero && (
            <div className="platform-content-detail-card__header">
              <h2 className="platform-content-detail-card__title">{article.title}</h2>
            </div>
          )}

          <div className="platform-content-detail__body">
            {!useHero && (
              <div className="platform-content-detail__meta">
                <ContentUnitTag item={article} unitNames={unitNames} />
                {placementLabel && (
                  <>
                    <span className="platform-content-detail__meta-separator">·</span>
                    <span>{placementLabel}</span>
                  </>
                )}
                <span className="platform-content-detail__meta-separator">·</span>
                <span>Ustvaril {article.displayName || 'Anonimen'}</span>
                {article.createdAt && (
                  <>
                    <span className="platform-content-detail__meta-separator">·</span>
                    <span>{formatDate(article.createdAt)}</span>
                  </>
                )}
              </div>
            )}

            {isAdmin && (
              <ArticleAdminControls
                article={article}
                tenantId={tenantId}
                tenantConfig={config}
                className="article-detail-admin"
                onSaved={(grouping) => {
                  setArticle((prev) => ({ ...prev, ...grouping }));
                }}
              />
            )}

            {article.description && (
              <div className="platform-content-detail__content">
                <p className="article-detail__description">{article.description}</p>
              </div>
            )}

            <ContentAttachmentsActions item={article} className="platform-content-detail__actions" />
          </div>
        </div>

        {!useHero && article.thumbnailUrl && (
          <div className="platform-content-detail-card">
            <div className="platform-content-detail-card__header">
              <h2 className="platform-content-detail-card__title">Slika</h2>
            </div>
            <div className="platform-content-detail__media platform-content-detail__media--bottom">
              <img src={article.thumbnailUrl} alt="" loading="lazy" />
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

export default ArticleDetail;
